import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Send, Clock, Sparkles, CheckCircle2, Share2, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { getSkillLabel, SKILL_AREAS } from '../data/skillAreas';
import SkillScorePanel from '../components/SkillScorePanel';

const SAMPLE_CHALLENGE = {
  title: 'Make AI disagree with you',
  description: 'Learn how to expose weak assumptions in your own thinking by asking AI to argue the opposite side.',
  prompt_text: 'Pick a strong opinion you hold about your work or life. Write a prompt that asks AI to argue against your position as convincingly as possible. The goal is to find the weakest points in your own thinking.',
  duration_minutes: 7,
  skill_areas: ['critique', 'reasoning'],
  rubric: 'Strong submissions include: (1) a clear statement of the original opinion, (2) a prompt that explicitly asks for the strongest counter-arguments, (3) constraints that prevent the AI from being agreeable, (4) a specific request for evidence or reasoning.',
};

interface GradeResult {
  score: number;
  feedback: string;
  skill_deltas: Record<string, number>;
  challenge: { title: string; skill_areas: string[] };
}

export default function TryChallengePage() {
  useDocumentMeta({
    title: 'Try a Challenge — Free AI Grading, No Signup | PromptGPT',
    description: 'Complete one full AI challenge free. Write your response, get graded by AI, see your score and a preview of your skill profile across 10 AI skill areas. No signup required.',
    canonical: 'https://promptgpt.ai/try',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'PromptGPT Free Challenge',
      applicationCategory: 'EducationalApplication',
      operatingSystem: 'Web',
      url: 'https://promptgpt.ai/try',
      description: 'Complete one full AI challenge with AI grading — free, no signup required. See your score and a preview of your skill profile across 10 AI skill areas.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'One free challenge with AI grading. No signup required.',
      },
    },
  });

  const [submission, setSubmission] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<GradeResult | null>(null);

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/grade-challenge`;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const handleSubmit = async () => {
    if (submission.trim().length < 10) {
      toast.error('Write at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey || '',
          'Authorization': `Bearer ${anonKey || ''}`,
        },
        body: JSON.stringify({ anonymous: true, submission: submission.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Grading failed');
        return;
      }
      setResult(data);
    } catch {
      toast.error('Grading failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = () => {
    if (!result) return;
    const text = `I scored ${result.score} on "${SAMPLE_CHALLENGE.title}" on PromptGPT. Try it free at promptgpt.ai.`;
    navigator.clipboard.writeText(text).then(
      () => toast.success('Score copied to clipboard'),
      () => toast.error('Could not copy'),
    );
  };

  // Build a sample skill profile from the result deltas
  const sampleScores: Record<string, number> = {};
  if (result) {
    for (const area of SKILL_AREAS) {
      const delta = result.skill_deltas[area] || 0;
      sampleScores[area] = delta > 0 ? 30 + delta * 4 : Math.floor(Math.random() * 20) + 10;
    }
  }

  // Find two weakest skills
  const weakestSkills = SKILL_AREAS
    .map((s) => ({ ...s, score: sampleScores[s.key] ?? 0 }))
    .sort((a, b) => a.score - b.score)
    .slice(0, 2);

  return (
    <div className="bg-warm-50 min-h-screen">
      <div className="container-app py-10 max-w-2xl">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-sm text-ink-500 hover:text-ink-900">PromptGPT</Link>
            <span className="text-ink-300">/</span>
            <span className="text-sm text-ink-500">Try a challenge</span>
          </div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3 bg-white border border-ink-100 rounded-full px-3 py-1.5">
            Free · No signup needed
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-tight">
            {SAMPLE_CHALLENGE.title}
          </h1>
          <p className="text-ink-600 text-lg leading-relaxed">{SAMPLE_CHALLENGE.description}</p>
          <div className="flex items-center gap-3 mt-4">
            <span className="inline-flex items-center gap-1 text-xs text-ink-500">
              <Clock className="w-3.5 h-3.5" /> {SAMPLE_CHALLENGE.duration_minutes} min
            </span>
            {SAMPLE_CHALLENGE.skill_areas.map((area) => (
              <span key={area} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 border border-accent-100">
                {getSkillLabel(area)}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white border border-ink-100 rounded-2xl p-6 mb-6">
          <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">Your task</div>
          <p className="text-ink-800 leading-relaxed text-[17px]">{SAMPLE_CHALLENGE.prompt_text}</p>
        </div>

        {result ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Score reveal */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="bg-white border border-ink-100 rounded-2xl p-6"
            >
              <div className="flex items-end justify-between mb-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-1">Your score</div>
                  <motion.div
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.1, type: 'spring', bounce: 0.5 }}
                    className={`font-display text-7xl font-semibold leading-none ${
                      result.score >= 90 ? 'text-mint-700' :
                      result.score >= 70 ? 'text-accent-700' :
                      result.score >= 50 ? 'text-sun-600' :
                      'text-coral-600'
                    }`}
                  >
                    {result.score}
                  </motion.div>
                </div>
                <button
                  onClick={handleShare}
                  className="btn-secondary"
                  title="Copy score to clipboard"
                >
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </div>
              {result.feedback && (
                <p className="text-ink-700 leading-relaxed text-sm border-t border-ink-100 pt-4">{result.feedback}</p>
              )}
            </motion.div>

            {/* Full skill panel preview */}
            <div className="bg-white border border-ink-100 rounded-2xl p-6">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-1">Your AI Skill Score preview</div>
              <p className="text-xs text-ink-400 mb-4">Here is what your full skill profile would look like. Sign up to track all 10 areas.</p>
              <SkillScorePanel scores={sampleScores} />
            </div>

            {/* Weakest skills to improve */}
            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-accent-600" />
                <span className="font-display text-lg">What to improve next</span>
              </div>
              <p className="text-sm text-ink-600 mb-4">Your two lowest skill areas. Create an account to get challenges that target these specifically.</p>
              <div className="space-y-2">
                {weakestSkills.map((s) => (
                  <div key={s.key} className="flex items-center justify-between">
                    <span className="text-sm text-ink-700">{s.label}</span>
                    <span className="text-sm font-semibold text-coral-600">{s.score}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Signup CTA */}
            <div className="bg-ink-900 text-warm-50 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-mint-400" />
                <span className="font-display text-lg">Save your score</span>
              </div>
              <p className="text-warm-300 text-sm leading-relaxed mb-4">
                Create an account to save your score, track all 10 skill areas, and get a new challenge every day.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/login?mode=signup" className="btn-primary">
                  Save your score <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="btn-secondary">
                  See pricing
                </Link>
              </div>
            </div>

            <div className="text-center pt-2">
              <Link to="/" className="text-sm text-ink-500 hover:text-ink-900">
                Back to home
              </Link>
            </div>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold block mb-2">
                Write your response
              </label>
              <textarea
                value={submission}
                onChange={(e) => setSubmission(e.target.value)}
                placeholder="Write your prompt here. Be specific — describe your opinion, then craft a prompt that asks AI to argue against it."
                rows={8}
                className="w-full border border-ink-200 rounded-xl px-4 py-3 text-ink-800 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition"
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-ink-400">Free challenge · graded by AI · no signup</p>
                <span className={`text-xs font-mono ${submission.length >= 10 ? 'text-mint-700' : 'text-ink-400'}`}>
                  {submission.length} / 10+
                </span>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || submission.trim().length < 10}
              className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Grading…' : <>Submit for grading <Send className="w-4 h-4" /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
